import { useEffect, useRef, useState } from "react";
import { Box, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import Form from "./Form";
import { createNewFleet } from "../../../actions/inventory/fleet.action";
import fleetFormSchema from "./Validation.jsx";

const AddFleet = () => {

  const defaultFleetValues = {
    // ── Step 1: Vehicle Segment ──────────────────────────────────
    vehicleType: "",
    model: "",
    manufacturer: "",
    year: "",
    regEndDate: "",
    vehicleColor: "",
    seatingCapacity: "",
    fuelType: "",
    gpsTracking: false,
    roofCarrier: false,
    vehicleNotes: "",
    vehicleLocationCity: "",        // NEW: Vehicle Location City
    vehicleLocationState: "",       // NEW: Vehicle Location State
    registrationNumber: "",
    registrationFront: "",
    registrationBack: "",

    // ── Step 2: Insurance / Documents ────────────────────────────
    insuranceProvider: "",
    policyNumber: "",
    insuranceExpiryDate: "",
    fitnessEndDate: "",
    insuranceImage: "",
    fitnessCertificate: "",
    pucEndDate: "",
    permitEndDate: "",
    pucCertificate: "",
    permitCertificate: "",
    taxPaidTillDate: "",
    vehicleNumber: "",
    taxDocument: "",
    vehicleImage: "",

    // ── Step 3: Owner / Status ────────────────────────────────────
    ownerType: "",
    ownerId: "",
    someoneElseVehicle: false,
    realOwnerName: "",
    ownerPhoto: "",
    ownershipAgreement: "",
    operationalStatus: "",
    administrativeStatus: "",
  };

  const [files, setFiles] = useState({});
  const [initialValues, setInitialValues] = useState(defaultFleetValues);
  const toast = useToast();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const handleSubmit = async (values) => {
    try {
      const merged = { ...values, ...files };

      delete merged.ownerDetails;

      const formData = new FormData();

      const fileFields = new Set([
        "registrationFront",
        "registrationBack",
        "insuranceImage",
        "fitnessCertificate",
        "pucCertificate",
        "permitCertificate",
        "taxDocument",
        "vehicleImage",
        "ownerPhoto",
        "ownershipAgreement",
      ]);

      Object.entries(merged).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;

        // File handling
        if (fileFields.has(key)) {
          if (value instanceof File) {
            formData.append(key, value);
          } else if (Array.isArray(value) && value[0] instanceof File) {
            formData.append(key, value[0]);
          }
          return;
        }

        // Number fields
        if (["year", "seatingCapacity", "administrativeStatus"].includes(key)) {
          const num = Number(value);
          if (!Number.isNaN(num)) formData.append(key, num);
          return;
        }

        // Date fields
        if (key.toLowerCase().includes("date") || key === "taxPaidTillDate") {
          const d = new Date(value);
          if (!isNaN(d.getTime())) formData.append(key, d.toISOString());
          return;
        }

        // Boolean fields
        if (typeof value === "boolean") {
          formData.append(key, value);
          return;
        }

        // String fallback (includes vehicleLocationCity, vehicleLocationState)
        formData.append(key, value);
      });

      const res = await createNewFleet(formData);
      console.log("API Response:", res);

      if (res?.success) {
        toast({
          title: "Success",
          description: "New fleet added successfully",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        localStorage.removeItem("AddFleetFormDraft");
        navigate("/fleet-management/vehicles");
      }
    } catch (err) {
      console.error("Submission Error:", err);
      toast({
        title: "Error",
        description: err.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Load draft
  useEffect(() => {
    const saved = localStorage.getItem("AddFleetFormDraft");
    if (saved && saved !== "undefined") {
      try {
        const parsed = JSON.parse(saved);
        setInitialValues({
          ...defaultFleetValues,
          ...parsed,
          gpsTracking: parsed.gpsTracking ?? false,
          roofCarrier: parsed.roofCarrier ?? false,
          ownerPhoto: "",           // never restore file fields from draft
          ownershipAgreement: "",
        });
      } catch {
        localStorage.removeItem("AddFleetFormDraft");
      }
    }
  }, []);

  return (
    <Box px={6} py={8}>
      <BreadCrumbMaker
        items={[
          { link: "", title: "Fleet Management" },
          { link: "/fleet-management/vehicles", title: "Vehicle" },
          { link: "/fleet-management/vehicles/add", title: "Add Vehicle", on: true },
        ]}
      />
      <Form
        initialValues={initialValues}
        files={files}
        setFiles={setFiles}
        handleSubmit={handleSubmit}
        formRef={formRef}
        validationSchema={fleetFormSchema}
      />
    </Box>
  );
};

export default AddFleet;
